import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Check } from "lucide-react";
import { api } from "../lib/api";

/**
 * TIME-1 — today's questions at the punch. Awareness, not a quiz: a statement is ticked, a
 * yes/no is one tap, free text is optional, and an answer that matters is sent to the foreman
 * WITHOUT ever stopping the punch.
 */
export default function PunchQuestions({ questions, answers, onChange, projectId, prefix }) {
  if (!questions?.length) return null;
  return (
    <div className="space-y-2" data-testid={`${prefix}-questions`}>
      {questions.map((q) => (
        <QuestionRow key={q.id} q={q} value={answers[q.id] || {}} projectId={projectId}
                     prefix={prefix}
                     onChange={(patch) => onChange({ ...answers,
                       [q.id]: { ...(answers[q.id] || {}), ...patch } })} />
      ))}
    </div>
  );
}

function QuestionRow({ q, value, onChange, projectId, prefix }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const flagged = q.type === "yes_no" && q.flag_answer
    && (value.answer || "").toLowerCase() === q.flag_answer;

  const attach = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("project_id", projectId || "");
      form.append("file", file);
      const { data } = await api.post("/completions/photos", form,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ photo_path: data.storage_path });
      toast.success("Photo attached");
    } catch { toast.error("Photo did not upload — try again"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-md border p-2 ${flagged ? "border-amber-300 bg-amber-50"
                                                      : "border-gray-200 bg-white"}`}
         data-testid={`${prefix}-question-${q.id}`}>
      {q.type === "ack" && (
        <label className="flex items-start gap-2 text-sm text-gray-800">
          <input type="checkbox" className="mt-1" data-testid={`${prefix}-ack-${q.id}`}
                 checked={!!value.answer}
                 onChange={(e) => onChange({ answer: e.target.checked ? "acknowledged" : "" })} />
          <span>{q.text}</span>
        </label>
      )}
      {q.type === "yes_no" && (
        <div>
          <p className="text-sm text-gray-800">{q.text}</p>
          <div className="mt-1.5 flex gap-2">
            {["yes", "no"].map((v) => (
              <button key={v} type="button" onClick={() => onChange({ answer: v })}
                      data-testid={`${prefix}-${v}-${q.id}`}
                      className={`h-9 px-4 rounded-md border text-sm font-semibold capitalize ${
                        (value.answer || "") === v
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white border-gray-300 text-gray-700"}`}>
                {v}
              </button>
            ))}
          </div>
          {flagged && (
            <p className="mt-1 text-[11px] text-amber-900" data-testid={`${prefix}-flagged-${q.id}`}>
              Your foreman will see this on your timesheet — you can still clock in.
            </p>
          )}
        </div>
      )}
      {q.type === "text" && (
        <label className="block text-sm text-gray-800">
          {q.text}
          <input value={value.answer || ""} onChange={(e) => onChange({ answer: e.target.value })}
                 placeholder="optional" data-testid={`${prefix}-text-${q.id}`}
                 className="mt-1 w-full h-9 border border-gray-300 rounded-md px-2 text-sm" />
        </label>
      )}
      {q.photo_required && (
        <div className="mt-1.5 flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" capture="environment"
                 className="hidden" data-testid={`${prefix}-photo-input-${q.id}`}
                 onChange={(e) => attach(e.target.files?.[0])} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
                  data-testid={`${prefix}-photo-${q.id}`}
                  className="h-8 px-3 rounded-md border border-gray-300 text-xs inline-flex items-center gap-1 bg-white">
            {value.photo_path ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                              : <Camera className="w-3.5 h-3.5" />}
            {value.photo_path ? "Photo attached" : busy ? "Uploading…" : "Photo required"}
          </button>
        </div>
      )}
    </div>
  );
}
